import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ethers } from 'ethers';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useWriteContract } from '../hooks/useWriteContract';
import { useContractQuery } from '../hooks/useContractQuery';
import {
  useFirmCount,
  useIsOracle,
  AUDIT_CATEGORIES,
  SENIORITY_LEVELS,
  FIRM_STATUSES,
  INCIDENT_SEVERITIES,
  type FirmRow,
} from '../hooks/useAuditorRegistry';

/**
 * Compliance page (#7) — role-gated panel for oracle / owner operations on
 * AuditorRegistryV2. Surfaces the four privileged write paths:
 *
 *    issueAttestation(firmId, certKey, evidenceHash, expiresAt)  — onlyOracle
 *    revokeAttestation(firmId, certKey)                          — onlyOracle
 *    grantDsmbVeto(firmId)  / revokeDsmbVeto(firmId)             — onlyOracle
 *    slash(firmId, severity, incidentHash)                       — onlyOwner
 *
 * The page renders even for non-privileged wallets so admins can SEE the
 * surface, but the action buttons are disabled and the contract will revert
 * anyway. Predefined certKey library mirrors typical regulated-sector
 * attestations (GxP, ISO 17025, KYB, etc.) — adding a custom certKey is
 * supported via a manual hash input.
 */

const CERT_KEYS = [
  { label: 'GxP',          desc: 'Good x Practice (GLP/GCP/GMP umbrella)' },
  { label: 'ISO_17025',    desc: 'General requirements for testing & calibration laboratories' },
  { label: 'KYB',          desc: 'Know Your Business — beneficial ownership disclosed' },
  { label: 'GMP',          desc: 'Good Manufacturing Practice' },
  { label: 'MDR',          desc: 'Medical Device Regulation compliance (EU)' },
  { label: 'CFR_21_PART_11', desc: '21 CFR Part 11 — electronic records / signatures (FDA)' },
  { label: 'CLIA',         desc: 'Clinical Laboratory Improvement Amendments (US)' },
] as const;

/** Map a known cert label to its bytes32 hash. */
function certKeyHash(label: string): string {
  return ethers.keccak256(ethers.toUtf8Bytes(label));
}

export default function Compliance() {
  const account = useAccount();
  const contracts = useReadContracts();
  const { data: countBn } = useFirmCount();
  const N = countBn !== undefined ? Number(countBn) : 0;

  const isOracle = useIsOracle(account);
  const owner = useContractQuery<string>(['registry', 'owner']);

  const isContractOwner = !!account && !!owner.data &&
    owner.data.toLowerCase() === account.toLowerCase();
  const isOracleWallet = !!isOracle.data;
  const hasAnyPriv = isOracleWallet || isContractOwner;

  // Fan-out: read every firm row for the picker.
  const firmQueries = useQueries({
    queries: Array.from({ length: N }, (_, i) => ({
      queryKey: ['metha', 'registry', 'getFirm', i] as const,
      queryFn: async () => (await contracts.registry.getFirm(BigInt(i))) as unknown as FirmRow,
      staleTime: 30_000,
    })),
  });

  const firms = useMemo(() => {
    return firmQueries
      .map((q, i) => (q.data ? { id: i, row: q.data } : null))
      .filter((x): x is { id: number; row: FirmRow } => x !== null);
  }, [firmQueries]);

  return (
    <Page ctxLabel="compliance" ctx="auditor" title="Metha · Compliance" docsPath="guides/dsmb" docsLabel="DSMB / compliance guide">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Compliance' }]} />

      <div className="ph">
        <div>
          <h1>Compliance <span className="acc">/ oracle &amp; owner ops</span></h1>
          <p className="lede">
            Privileged write paths on <span className="mono">AuditorRegistryV2</span>: attestation
            lifecycle (GxP, ISO 17025, KYB, etc.), DSMB veto grants for clinical lanes, and
            slashing for compliance incidents. Buttons are role-gated — your wallet must be an
            oracle (for attestations + DSMB) or the contract owner (for slashing).
          </p>
        </div>
        <div className="meta">
          <span>YOUR ROLE <b style={{ color: hasAnyPriv ? 'var(--green)' : 'var(--muted)' }}>
            {!account ? 'connect wallet' :
              isContractOwner && isOracleWallet ? 'owner + oracle' :
              isContractOwner ? 'owner' :
              isOracleWallet ? 'oracle' :
              'no privileges'}
          </b></span>
          <span>FIRMS ON FILE <b>{N}</b></span>
        </div>
      </div>

      {!hasAnyPriv && (
        <div className="warn mb-14">
          Your wallet is neither an oracle nor the contract owner. Forms are visible but the
          contract will revert all writes. Use this view to inspect the queue; ask whoever holds
          the oracle / owner role to execute.
        </div>
      )}

      <IssueAttestationPanel firms={firms} disabled={!isOracleWallet} />
      <RevokeAttestationPanel firms={firms} disabled={!isOracleWallet} />
      <DsmbVetoPanel firms={firms} disabled={!isOracleWallet} />
      <SlashPanel firms={firms} disabled={!isContractOwner} />
    </Page>
  );
}

/* -------------------- subsection panels -------------------- */

function FirmPicker({
  firms, value, onChange, filter,
}: {
  firms: Array<{ id: number; row: FirmRow }>;
  value: number | null;
  onChange: (id: number | null) => void;
  /** Optional filter — only show firms that match. */
  filter?: (f: { id: number; row: FirmRow }) => boolean;
}) {
  const list = filter ? firms.filter(filter) : firms;
  return (
    <div className="input" style={{ background: 'transparent', borderColor: 'var(--line)', width: '100%' }}>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={{ width: '100%', background: 'transparent', color: 'var(--ink)', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'inherit' }}
      >
        <option value="" style={{ background: 'var(--panel)', color: 'var(--ink)' }}>— select firm —</option>
        {list.map(({ id, row }) => (
          <option key={id} value={id} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>
            #{String(id).padStart(3, '0')} · {row.name || short(row.wallet)} · {SENIORITY_LEVELS[Number(row.seniority)]} · {FIRM_STATUSES[Number(row.status)]}
          </option>
        ))}
      </select>
    </div>
  );
}

function CertKeyPicker({
  value, onChange, customHash, onCustomHashChange,
}: {
  value: string | 'custom' | '';
  onChange: (v: string | 'custom' | '') => void;
  customHash: string;
  onCustomHashChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as string | 'custom' | '')}
          style={{ width: '100%', background: 'transparent', color: 'var(--ink)', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'inherit' }}
        >
          <option value="" style={{ background: 'var(--panel)', color: 'var(--ink)' }}>— select cert key —</option>
          {CERT_KEYS.map(({ label, desc }) => (
            <option key={label} value={label} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>
              {label} — {desc}
            </option>
          ))}
          <option value="custom" style={{ background: 'var(--panel)', color: 'var(--ink)' }}>
            Custom bytes32…
          </option>
        </select>
      </div>
      {value === 'custom' && (
        <input
          className="mono"
          style={{ padding: '4px 8px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
          placeholder="0x-prefixed bytes32 cert key"
          value={customHash}
          onChange={(e) => onCustomHashChange(e.target.value)}
        />
      )}
      {value && value !== 'custom' && value !== '' && (
        <div className="mono dim" style={{ fontSize: 10 }}>
          hash = {short(certKeyHash(value))}
        </div>
      )}
    </div>
  );
}

function resolveCertKey(value: string | 'custom' | '', customHash: string): string | null {
  if (!value) return null;
  if (value === 'custom') {
    return /^0x[0-9a-fA-F]{64}$/.test(customHash.trim()) ? customHash.trim() : null;
  }
  return certKeyHash(value);
}

function IssueAttestationPanel({
  firms, disabled,
}: { firms: Array<{ id: number; row: FirmRow }>; disabled: boolean }) {
  const [firmId, setFirmId] = useState<number | null>(null);
  const [certValue, setCertValue] = useState<string | 'custom' | ''>('');
  const [customCertHash, setCustomCertHash] = useState('');
  const [evidenceHash, setEvidenceHash] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => {
    // default 1 year from now
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });

  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const certHash = resolveCertKey(certValue, customCertHash);
  const evHashOk = /^0x[0-9a-fA-F]{64}$/.test(evidenceHash.trim());
  const expiresAtSec = expiresAt ? Math.floor(new Date(expiresAt).getTime() / 1000) : 0;
  const futureExpiry = expiresAtSec > Math.floor(Date.now() / 1000);
  const canSubmit = !disabled && !submitting && firmId !== null && certHash !== null && evHashOk && futureExpiry;

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Issue attestation <Pill tone="purple">oracle</Pill></h3>
        <span className="src"><code>registry.issueAttestation(firmId, certKey, evidenceHash, expiresAt)</code></span>
      </div>
      <div className="p-body">
        <div className="form-row r2">
          <div>
            <span className="field-l">Firm</span>
            <FirmPicker
              firms={firms}
              value={firmId}
              onChange={setFirmId}
              filter={(f) => Number(f.row.status) === 1 /* Active */}
            />
          </div>
          <div>
            <span className="field-l">Cert key</span>
            <CertKeyPicker
              value={certValue}
              onChange={setCertValue}
              customHash={customCertHash}
              onCustomHashChange={setCustomCertHash}
            />
          </div>
        </div>
        <div className="form-row r2">
          <div>
            <span className="field-l">Evidence hash (bytes32)</span>
            <div className="input"><input
              className="mono"
              value={evidenceHash}
              onChange={(e) => setEvidenceHash(e.target.value)}
              placeholder="0x… (keccak256 of off-chain doc)"
            /></div>
            <p className="help">Hash of the off-chain audit report, certificate scan, or compliance doc. Typically stored in Supabase / IPFS.</p>
          </div>
          <div>
            <span className="field-l">Expires at</span>
            <div className="input"><input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            /></div>
            <p className="help">Annual renewal is the typical cadence for GxP / ISO recerts.</p>
          </div>
        </div>
        <button
          className={`btn btn-prim btn-block mt-14${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          onClick={() => write({
            contract: 'registry',
            fn: 'issueAttestation',
            args: [BigInt(firmId!), certHash!, evidenceHash.trim(), BigInt(expiresAtSec)],
            options: {
              pendingTitle: `Issuing attestation for firm #${firmId}…`,
              successTitle: `Attestation issued for firm #${firmId}`,
              invalidate: [['metha', 'registry']],
            },
          })}
        >
          {submitting ? 'Submitting…' : 'Issue attestation'}
        </button>
      </div>
    </div>
  );
}

function RevokeAttestationPanel({
  firms, disabled,
}: { firms: Array<{ id: number; row: FirmRow }>; disabled: boolean }) {
  const [firmId, setFirmId] = useState<number | null>(null);
  const [certValue, setCertValue] = useState<string | 'custom' | ''>('');
  const [customCertHash, setCustomCertHash] = useState('');

  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const certHash = resolveCertKey(certValue, customCertHash);
  const canSubmit = !disabled && !submitting && firmId !== null && certHash !== null;

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Revoke attestation <Pill tone="purple">oracle</Pill></h3>
        <span className="src"><code>registry.revokeAttestation(firmId, certKey)</code></span>
      </div>
      <div className="p-body">
        <div className="form-row r2">
          <div>
            <span className="field-l">Firm</span>
            <FirmPicker firms={firms} value={firmId} onChange={setFirmId} />
          </div>
          <div>
            <span className="field-l">Cert key to revoke</span>
            <CertKeyPicker
              value={certValue}
              onChange={setCertValue}
              customHash={customCertHash}
              onCustomHashChange={setCustomCertHash}
            />
          </div>
        </div>
        <button
          className={`btn btn-block mt-14${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          onClick={() => write({
            contract: 'registry',
            fn: 'revokeAttestation',
            args: [BigInt(firmId!), certHash!],
            options: {
              pendingTitle: `Revoking attestation for firm #${firmId}…`,
              successTitle: `Attestation revoked for firm #${firmId}`,
              invalidate: [['metha', 'registry']],
            },
          })}
        >
          {submitting ? 'Submitting…' : 'Revoke attestation'}
        </button>
      </div>
    </div>
  );
}

function DsmbVetoPanel({
  firms, disabled,
}: { firms: Array<{ id: number; row: FirmRow }>; disabled: boolean }) {
  const [firmId, setFirmId] = useState<number | null>(null);
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const selectedFirm = firmId !== null ? firms.find((f) => f.id === firmId) : undefined;
  const currentlyHas = !!selectedFirm?.row.dsmbVetoRights;
  const fn = currentlyHas ? 'revokeDsmbVeto' : 'grantDsmbVeto';
  const canSubmit = !disabled && !submitting && firmId !== null;

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>DSMB veto rights <Pill tone="purple">oracle</Pill></h3>
        <span className="src"><code>registry.{fn}(firmId)</code></span>
      </div>
      <div className="p-body">
        <div className="form-row">
          <span className="field-l">Firm (must hold ClinicalSafety category)</span>
          {/* Filter to firms that hold ClinicalSafety. We don't know hasCategory
              for each firm here without an extra fan-out, so for the mockup we
              show all Active firms and let the contract enforce the guard. */}
          <FirmPicker
            firms={firms}
            value={firmId}
            onChange={setFirmId}
            filter={(f) => Number(f.row.status) === 1}
          />
        </div>
        {selectedFirm && (
          <div className="estim">
            <span>CURRENT STATE</span>
            <b style={{ color: currentlyHas ? 'var(--green)' : 'var(--muted)' }}>
              {currentlyHas ? 'has DSMB veto' : 'no DSMB veto'}
            </b>
          </div>
        )}
        <button
          className={`btn btn-block mt-14${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          style={canSubmit && !currentlyHas ? { background: 'var(--purple)', borderColor: 'var(--purple)', color: '#fff' } : undefined}
          onClick={() => write({
            contract: 'registry',
            fn,
            args: [BigInt(firmId!)],
            options: {
              pendingTitle: `${currentlyHas ? 'Revoking' : 'Granting'} DSMB veto for firm #${firmId}…`,
              successTitle: `DSMB veto ${currentlyHas ? 'revoked from' : 'granted to'} firm #${firmId}`,
              invalidate: [['metha', 'registry']],
            },
          })}
        >
          {submitting ? 'Submitting…' :
            firmId === null ? 'Select a firm' :
            currentlyHas ? `Revoke DSMB from #${firmId}` :
            `Grant DSMB to #${firmId}`}
        </button>
        <p className="help mt-14" style={{ marginBottom: 0 }}>
          Contract enforces that the firm must hold the <span className="mono">ClinicalSafety</span> category
          for the grant to succeed. Revoking ClinicalSafety from the firm later also auto-revokes DSMB.
        </p>
      </div>
    </div>
  );
}

function SlashPanel({
  firms, disabled,
}: { firms: Array<{ id: number; row: FirmRow }>; disabled: boolean }) {
  const [firmId, setFirmId] = useState<number | null>(null);
  const [severityIdx, setSeverityIdx] = useState<number>(0); // Minor
  const [incidentHash, setIncidentHash] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const selectedFirm = firmId !== null ? firms.find((f) => f.id === firmId) : undefined;
  const incidentHashOk = /^0x[0-9a-fA-F]{64}$/.test(incidentHash.trim());
  const expectedConfirm = severityIdx === 2 ? 'SLASH CRITICAL' : 'CONFIRM';
  const confirmedText = confirmText.trim() === expectedConfirm;
  const canSubmit = !disabled && !submitting && firmId !== null && incidentHashOk && confirmedText;

  // Slashing math preview (display-only — contract is authoritative).
  const stakeMeth = selectedFirm ? weiToEth(selectedFirm.row.stake) : 0;
  const previewPct =
    severityIdx === 0 ? '3% / 5% / 10% (escalating per minor strike)' :
    severityIdx === 1 ? '10% / 25% / 50% (escalating per major strike)' :
    '100% + permanent ban';
  const previewAmount =
    severityIdx === 0 ? `~${(stakeMeth * 0.03).toFixed(2)}–${(stakeMeth * 0.10).toFixed(2)} METH` :
    severityIdx === 1 ? `~${(stakeMeth * 0.10).toFixed(2)}–${(stakeMeth * 0.50).toFixed(2)} METH` :
    `${stakeMeth.toFixed(2)} METH + ban`;

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Slash firm <Pill tone="red">owner</Pill></h3>
        <span className="src"><code>registry.slash(firmId, severity, incidentHash)</code></span>
      </div>
      <div className="p-body">
        <div className="form-row r2">
          <div>
            <span className="field-l">Firm (Active or Frozen)</span>
            <FirmPicker
              firms={firms}
              value={firmId}
              onChange={setFirmId}
              filter={(f) => Number(f.row.status) === 1 || Number(f.row.status) === 2}
            />
          </div>
          <div>
            <span className="field-l">Severity</span>
            <div className="input">
              <select
                value={severityIdx}
                onChange={(e) => setSeverityIdx(Number(e.target.value))}
                style={{ width: '100%', background: 'transparent', color: 'var(--ink)', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'inherit' }}
              >
                {INCIDENT_SEVERITIES.map((s, i) => (
                  <option key={s} value={i} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>
                    {s} — {i === 0 ? 'minor compliance miss' : i === 1 ? 'major procedural breach' : 'fraud / gross negligence'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="form-row">
          <span className="field-l">Incident hash (bytes32 — keccak of off-chain incident report)</span>
          <div className="input"><input
            className="mono"
            value={incidentHash}
            onChange={(e) => setIncidentHash(e.target.value)}
            placeholder="0x…"
          /></div>
        </div>

        {selectedFirm && (
          <div style={{ background: 'var(--panel-2)', padding: 12, borderRadius: 5, marginBottom: 10 }}>
            <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
              Slashing preview · authoritative math is on-chain
            </div>
            <div className="estim"><span>FIRM STAKE</span><b>{fmt.meth(stakeMeth)} METH</b></div>
            <div className="estim"><span>SEVERITY</span><b>{INCIDENT_SEVERITIES[severityIdx]}</b></div>
            <div className="estim"><span>BURN RANGE</span><b style={{ color: 'var(--red)' }}>{previewPct}</b></div>
            <div className="estim"><span>ESTIMATED</span><b style={{ color: 'var(--red)' }}>{previewAmount}</b></div>
            <div className="estim"><span>EXISTING STRIKES</span><b>—</b></div>
          </div>
        )}

        {firmId !== null && incidentHashOk && (
          <div className="form-row">
            <span className="field-l">
              Confirmation — type <span className="mono" style={{ color: 'var(--red)' }}>{expectedConfirm}</span> to enable the slash button
            </span>
            <div className="input"><input
              className="mono"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedConfirm}
            /></div>
          </div>
        )}

        <button
          className={`btn btn-block mt-14${canSubmit ? 'btn-prim' : ' btn-locked'}`}
          disabled={!canSubmit}
          style={canSubmit ? { background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' } : undefined}
          onClick={() => write({
            contract: 'registry',
            fn: 'slash',
            args: [BigInt(firmId!), severityIdx, incidentHash.trim()],
            options: {
              pendingTitle: `Slashing firm #${firmId} (${INCIDENT_SEVERITIES[severityIdx]})…`,
              successTitle: `Firm #${firmId} slashed (${INCIDENT_SEVERITIES[severityIdx]})`,
              invalidate: [['metha', 'registry']],
            },
          })}
        >
          {submitting ? 'Submitting…' : `Slash firm (${INCIDENT_SEVERITIES[severityIdx]})`}
        </button>

        <p className="help mt-14" style={{ marginBottom: 0, color: 'var(--dim)' }}>
          Slashing is destructive and on-chain irreversible. In production, this path should be
          invoked via OpenGovernance proposal rather than direct owner call — see roadmap §0.A.
          Critical slashes additionally Ban the firm permanently.
        </p>
      </div>
    </div>
  );
}

/* Help TS infer the unused imports as intentionally available */
void AUDIT_CATEGORIES;
